import { useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Sparkles, Send, Crown, Star, Gem, Users } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_TEMPLATE = "Hi {name} ✨ Exclusive offer for {plan_name} members only! {offer_text} — valid today only. Book: {booking_url}";

const TIERS = [
  { v: "all", label: "All Members", Icon: Users },
  { v: "silver", label: "Silver", Icon: Star },
  { v: "gold", label: "Gold", Icon: Crown },
  { v: "platinum", label: "Platinum", Icon: Gem },
];

/**
 * QuickFlashOffer — one-tap member-only WhatsApp blast.
 * POST /api/campaigns/flash with { tier_filter, message, offer_text }.
 */
export function QuickFlashOffer({ branchId }) {
  const [tier, setTier] = useState("gold");
  const [offerText, setOfferText] = useState("20% off any spa service");
  const [message, setMessage] = useState(DEFAULT_TEMPLATE);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!offerText.trim()) { toast.error("Add the offer text first"); return; }
    setSending(true);
    try {
      const r = await api.post("/campaigns/flash", {
        tier_filter: tier,
        message,
        offer_text: offerText,
        branch_id: branchId,
      });
      const data = unwrap(r);
      if (data.sent > 0) {
        toast.success(`Flash offer sent to ${data.sent} member${data.sent === 1 ? "" : "s"} ✨`);
      } else {
        toast.info(data.note || `No active ${tier === "all" ? "" : tier + " "}members to send to`);
      }
    } catch (e) {
      toast.error("Failed to send flash offer");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="glass-primary rounded-[20px] p-5 mb-4"
      style={{ borderImage: "linear-gradient(135deg,#D4A853,#8E6A1A) 1", borderWidth: 1.5, borderStyle: "solid" }}
      data-testid="quick-flash-offer"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-[var(--color-gold-deeper)]" />
            <h3 className="font-sora font-bold text-[16px] text-[var(--color-text-primary)]">Send a member-only offer</h3>
          </div>
          <p className="font-inter text-[12.5px] text-[var(--color-text-muted)]">
            One-tap WhatsApp blast to your members — no campaign draft needed.
          </p>
        </div>
      </div>

      {/* Tier selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {TIERS.map((t) => (
          <button
            key={t.v}
            onClick={() => setTier(t.v)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full font-sora font-semibold text-[11.5px] uppercase tracking-wider transition ${tier === t.v
              ? "bg-[var(--color-gold-mid)] text-white shadow-sm"
              : "glass-subtle text-[var(--color-text-secondary)] hover:text-[var(--color-gold-deeper)]"
            }`}
            data-testid={`flash-tier-${t.v}`}
          >
            <t.Icon size={11} /> {t.label}
          </button>
        ))}
      </div>

      {/* Offer text */}
      <div className="mb-3">
        <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">Offer text</label>
        <input
          value={offerText}
          onChange={(e) => setOfferText(e.target.value)}
          placeholder="e.g. 20% off any spa service"
          className="w-full glass-subtle rounded-lg px-3 py-2 outline-none font-inter text-[13.5px] focus:ring-2 focus:ring-[var(--color-gold-mid)]/40"
          data-testid="flash-offer-text"
        />
      </div>

      {/* Message template */}
      <div className="mb-4">
        <label className="block font-inter text-[11.5px] text-[var(--color-text-muted)] mb-1">
          Message template <span className="text-[var(--color-text-faint)]">· tokens: {"{name} {plan_name} {offer_text} {booking_url}"}</span>
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className="w-full glass-subtle rounded-lg px-3 py-2 outline-none font-inter text-[12.5px] leading-relaxed focus:ring-2 focus:ring-[var(--color-gold-mid)]/40"
          data-testid="flash-offer-message"
        />
      </div>

      <button
        onClick={send}
        disabled={sending}
        className="btn-3d btn-gold-3d btn-md w-full"
        data-testid="flash-offer-send-btn"
      >
        <Send size={14} /> {sending ? "Sending…" : "Send Flash Offer"}
      </button>
    </div>
  );
}
